// src/components/orders/BundleItemCard/BundleItemCard.jsx
import React from 'react';
import { Hash, Minus, Plus, X } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import BundleBadge from '../../ui/BundleBadge/BundleBadge';
import Dropdown from '../../ui/Dropdown/Dropdown';
import styles from './BundleItemCard.module.css';

const money = (n) => `Rs. ${(Number(n) || 0).toFixed(2)}`;

const BundleItemCard = ({ group, onQuantityChange, onRemove, crossSell = null, disabled = false }) => {
  const { t } = useLocalization();
  const qty = Number(group.bundleQuantity) || 1;
  const lockedByOther = !!crossSell?.lockedByOther;

  const renderCrossSell = () => {
    if (!crossSell) return null;
    const { checked, count, maxQty, lockedByOther, onChange } = crossSell;
    const disableNew = !checked && maxQty <= 0;
    const options = Math.max(count || 0, maxQty);
    return (
      <div className={styles.fieldGroup}>
        <label title={disableNew ? (t('ecomOrders.crossSellNoEligibleQty') || 'No new/increased quantity to cross-sell') : undefined}>
          {t('ecomOrders.crossSellBundles') || 'Cross Sell (bundles)'}
        </label>
        <div className={styles.crossSellControl}>
          <div className={styles.crossSellCheckboxBox}>
            <input
              type="checkbox"
              className={styles.crossSellCheckbox}
              checked={!!checked}
              disabled={disabled || lockedByOther || disableNew}
              onChange={(e) => onChange(e.target.checked, Math.max(1, Math.min(count || maxQty || 1, maxQty || 1)))}
            />
          </div>
          <Dropdown
            className={styles.crossSellQtyDropdown}
            panelClassName={styles.crossSellQtyPanel}
            value={checked ? (count || 1) : 0}
            onChange={(v) => v != null && onChange(true, Number(v))}
            options={checked
              ? Array.from({ length: Math.max(options, 1) }, (_, i) => ({ value: i + 1, label: String(i + 1) }))
              : [{ value: 0, label: '0' }]}
            disabled={disabled || !checked || lockedByOther}
            allowClear={false}
            size="compact"
          />
        </div>
      </div>
    );
  };

  return (
    <div className={styles.bundleCard}>
      <div className={styles.bundleTitleRow}>
        <div className={styles.bundleTitle}>
          <h4 className={styles.bundleName}>{group.bundleName || t('ecomOrders.bundle')}</h4>
          {group.bundleSku && (
            <span className={styles.bundleSku}>
              <Hash size={12} />
              {group.bundleSku}
            </span>
          )}
        </div>
        {onRemove && (
          <button
            type="button"
            className={styles.removeButton}
            onClick={onRemove}
            disabled={disabled || lockedByOther}
            title={t('ecomOrders.removeBundle') || 'Remove bundle'}
          >
            <X size={16} />
          </button>
        )}
      </div>

      <div className={styles.bundleHeader}>
        <div className={styles.badgeCell}>
          <BundleBadge variant="list" className={styles.cardBadge} />
        </div>

        <div className={styles.fieldGroup}>
          <label>
            {t('ecomOrders.bundleQuantity') || 'Bundle Qty'}
            {lockedByOther && <span className={styles.lockedField}>🔒</span>}
          </label>
          <div className={styles.quantityControl}>
            <button
              type="button"
              className={styles.quantityButton}
              onClick={() => onQuantityChange(qty - 1)}
              disabled={disabled || lockedByOther || qty <= 1}
            >
              <Minus size={14} />
            </button>
            <span className={styles.quantityValue}>{qty}</span>
            <button
              type="button"
              className={styles.quantityButton}
              onClick={() => onQuantityChange(qty + 1)}
              disabled={disabled || lockedByOther}
            >
              <Plus size={14} />
            </button>
          </div>
        </div>

        <div className={styles.fieldGroup}>
          <label>{t('ecomOrders.bundleTotal') || 'Bundle Total'}</label>
          <div className={styles.totalDisplay}>{money(group.total)}</div>
        </div>

        {renderCrossSell()}
      </div>

      <div className={styles.componentList}>
        <div className={`${styles.componentRow} ${styles.componentHeader}`}>
          <span>{t('ecomOrders.item') || 'Item'}</span>
          <span>{t('ecomOrders.quantity') || 'Quantity'}</span>
          <span className={styles.componentPrice}>{t('ecomOrders.unitPrice') || 'Unit Price'}</span>
          <span className={styles.componentDiscount}>{t('ecomOrders.lineDiscount') || 'Line Discount'}</span>
          <span className={styles.componentTotal}>{t('ecomOrders.lineTotal') || 'Line Total'}</span>
        </div>
        {group.items.map(item => {
          const lineTotal = (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0) - (Number(item.lineDiscount) || 0);
          return (
            <div key={item.id} className={styles.componentRow}>
              <span className={styles.componentName}>{item.displayName}</span>
              <span className={styles.componentQty}>× {Number(item.quantity)}</span>
              <span className={styles.componentPrice}>{money(item.unitPrice)}</span>
              <span className={styles.componentDiscount}>
                {Number(item.lineDiscount) > 0 ? `− ${money(item.lineDiscount)}` : ''}
              </span>
              <span className={styles.componentTotal}>
                {lineTotal <= 0 && <span className={styles.freeTag}>{t('ecomOrders.free') || 'FREE'}</span>}
                {money(lineTotal)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BundleItemCard;
